import { Link } from 'react-router-dom';

import { Icon } from '@/components';
import { cn } from '@/utils/styles';

import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { SidebarContentConfig } from '../../types';

export type MainSidebarContentProps = {
  readonly config: SidebarContentConfig;
  readonly isCollapsed: boolean;
  readonly className?: string;
};

export function MainSidebarContent({ config, isCollapsed, className }: MainSidebarContentProps) {
  return (
    <SidebarContent className={className}>
      {config.map((group) => (
        <SidebarGroup key={group.key}>
          <SidebarGroupLabel>{group.label}</SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              {group.items.map((item) => {
                const hasChildren = !!item.items?.length;

                if (hasChildren) {
                  return (
                    <Collapsible key={item.key} open={item.isOpen} onOpenChange={item.onOpenChange}>
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton isActive={item.isActive} tooltip={item.tooltip ?? item.label}>
                            {item.icon ? <Icon name={item.icon as any} /> : null}
                            <span>{item.label}</span>

                            <Icon
                              name="chevron-down"
                              className={cn(
                                'ml-auto size-4 transition-transform',
                                item.isOpen && 'rotate-180',
                                isCollapsed && 'hidden'
                              )}
                            />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items!.map((sub) => (
                              <SidebarMenuSubItem key={sub.key}>
                                <SidebarMenuSubButton asChild isActive={sub.isActive}>
                                  <Link to={sub.to}>
                                    <span>{sub.label}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }

                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton asChild isActive={item.isActive} tooltip={item.tooltip ?? item.label}>
                      <Link to={item.to!}>
                        {item.icon ? <Icon name={item.icon as any} /> : null}
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </SidebarContent>
  );
}
